import {
  Body,
  Body2,
  Header4,
  Title,
  Title1,
  Title4,
} from 'components/Typography';
import { Button } from '@dapp/multi-lib/sdaoui/Button';
import { useTranslation } from 'react-i18next';
import { Surface } from '@dapp/multi-lib/sdaoui/Surface';
import { ButtonIcon } from '@dapp/multi-lib/sdaoui/ButtonIcon';
import { Icon } from '@dapp/multi-lib/sdaoui/Icon';
import { ModalV2 } from '@dapp/multi-lib/ui/V2Modal';
import React, { useMemo, useState } from 'react';
import { HorizontalLine } from '@dapp/multi-lib/sdaoui/HorizontalLine';
import { getStaticAssetUrl } from 'utils/url';
import { useVaultListAcrossChains } from 'structure/pv2/dynavault/hooks/useVaultList';
import { useUserTotalAssets } from 'structure/pv2/vault/hooks/useUserTotalAssets';
import { formatUnits } from 'utils/units';
import { useConnection } from 'wagmi';
import { UserTotalBalance } from 'structure/pv2/dynavault/components/UserTotalBalance';
import { useVaultListOnChainDataAcrossChains } from 'structure/pv2/dynavault/hooks/useVaultListBlockchain';
import { formatUsd } from '@dapp/multi-lib/lib/formatters';
import { VAULT_LIST_REFETCH_INTERVAL } from 'structure/pv2/dynavault/constants';

export const TopBanner = () => {
  const connection = useConnection();

  const vaultListQuery = useVaultListAcrossChains();

  const tokens = useMemo(() => {
    return vaultListQuery.data
      ? vaultListQuery.data.map((vault) => ({
          address: vault.address,
          chainId: vault.chainId,
        }))
      : [];
  }, [vaultListQuery.data]);

  const userTotalAssetsQuery = useUserTotalAssets({
    userAddress: connection.address as `0x${string}`,
    tokens,
  });
  const vaultsOnChainDataQuery = useVaultListOnChainDataAcrossChains({
    vaultList: vaultListQuery.data ?? [],
    walletAddress: connection.address as `0x${string}`,
    query: {
      staleTime: VAULT_LIST_REFETCH_INTERVAL,
      refetchInterval: VAULT_LIST_REFETCH_INTERVAL,
      enabled: Boolean(vaultListQuery.data?.length),
    },
  });

  const balanceAcrossAllVaultsBN = userTotalAssetsQuery.data
    ? userTotalAssetsQuery.data.quotesTotal
    : BigInt(0);
  const balanceAcrossAllVaults = formatUnits(
    balanceAcrossAllVaultsBN,
    userTotalAssetsQuery.data?.decimals || 0
  );

  return (
    <div
      className='rounded-main mt-8 w-full border border-[#191A1E] bg-cover bg-top max-lg:mt-0'
      style={{
        backgroundImage: `url(${getStaticAssetUrl('/sfi/static/pv2/block_cave.jpeg)')}`,
      }}
    >
      <MobileTopBanner
        balance={balanceAcrossAllVaults}
        totalTvl={vaultsOnChainDataQuery.totalTvlUsd}
      />
      <DesktopTopBanner
        balance={balanceAcrossAllVaults}
        totalTvl={vaultsOnChainDataQuery.totalTvlUsd}
      />
    </div>
  );
};

interface TopBannerContentProps {
  balance: string;
  totalTvl: number;
}

const MobileTopBanner = (props: TopBannerContentProps) => {
  const { balance = '0', totalTvl } = props;

  const { t } = useTranslation('vault');

  const [modal, setModal] = useState({ id: 'index', open: false });

  return (
    <div className='flex flex-col gap-8 p-6 lg:hidden'>
      <div className='flex flex-row items-center justify-between'>
        <Title className='text-[1.5rem] font-light'>V A U L T S</Title>
        <ButtonIcon className='bg-transparent'>
          <Icon icon='open_in_new' color='white' width={16} height={16} />
        </ButtonIcon>
      </div>

      <VaultOverviewTotals
        balance={balance}
        totalTvl={totalTvl}
        className='mt-2'
      />

      <div className='flex flex-row gap-2'>
        <Button
          onClick={() => setModal({ id: 'index', open: true })}
          style='outline'
          size='small'
        >
          {t('INDEX')}
        </Button>
        <Button
          onClick={() => setModal({ id: 'yield', open: true })}
          style='outline'
          size='small'
        >
          {t('YIELD')}
        </Button>
      </div>
      <Modal
        open={modal.id === 'index' && modal.open}
        onChange={(open) => setModal({ id: 'index', open })}
        title={t('INDEX_VAULT')}
        description={t('INDEX_VAULT_DESC')}
      />
      <Modal
        open={modal.id === 'yield' && modal.open}
        onChange={(open) => setModal({ id: 'yield', open })}
        title={t('YIELD_VAULT')}
        description={t('YIELD_VAULT_DESC')}
      />
    </div>
  );
};

const DesktopTopBanner = (props: TopBannerContentProps) => {
  const { balance = '0', totalTvl } = props;
  const { t } = useTranslation('vault');
  return (
    <div className='hidden flex-row items-center justify-between p-6 lg:flex'>
      <div className='flex flex-col items-start gap-4 self-start'>
        <Title className='text-[2rem] font-light'>V A U L T S</Title>
        <Button style='outline' size='small'>
          {t('LEARN_MORE')}
        </Button>
        <VaultOverviewTotals
          balance={balance}
          totalTvl={totalTvl}
          className='mt-8'
        />
      </div>
      <div className='flex flex-row gap-8'>
        <Surface
          style='transparent'
          outline='none'
          className='flex max-w-[352px] flex-col gap-2 px-5'
        >
          <Title1>{t('INDEX_VAULT')}</Title1>
          <Body2 className='text-[#B4BCD0]'>{t('INDEX_VAULT_DESC')}</Body2>
        </Surface>
        <Surface
          style='transparent'
          outline='none'
          className='flex max-w-[352px] flex-col gap-2 px-5'
        >
          <Title1>{t('YIELD_VAULT')}</Title1>
          <Body2 className='text-[#B4BCD0]'>{t('YIELD_VAULT_DESC')}</Body2>
        </Surface>
      </div>
    </div>
  );
};

const VaultTotalValue = (props: { totalTvl: number }) => {
  const { totalTvl } = props;

  return (
    <div className='flex flex-col items-start gap-2 self-start'>
      <Title4 className='text-[1rem] font-thin leading-[1rem] text-[#B5B5B5] lg:text-[1.25rem] lg:leading-[1.25rem]'>
        TVL:
      </Title4>
      <Header4 className='text-[1.625rem] leading-[1.625rem] text-white lg:text-[2.25rem] lg:leading-[2.25rem]'>
        ${formatUsd(totalTvl)}
      </Header4>
    </div>
  );
};

const VaultOverviewTotals = (
  props: TopBannerContentProps & { className?: string }
) => {
  const { balance, totalTvl, className } = props;
  const connection = useConnection();
  const { t } = useTranslation('common');

  return (
    <div className={`flex flex-col items-start gap-2 ${className ?? ''}`}>
      <div className='flex flex-row flex-nowrap items-start gap-8 lg:gap-10'>
        <UserTotalBalance
          balance={balance}
          label='User Balance'
          showConnectionDescription={false}
        />
        <VaultTotalValue totalTvl={totalTvl} />
      </div>
      <Body2 className={connection.isConnected ? 'hidden' : 'text-[#EDFF6D]'}>
        {t('CONNECT_WALLET_DESCRIPTION')}
      </Body2>
    </div>
  );
};

type ModalProps = {
  open?: boolean;
  title: string;
  description: string;
  onChange: (open: boolean) => void;
};

const Modal = (props: ModalProps) => {
  const { open, title, description, onChange } = props;

  return (
    <ModalV2.Root modal={true} open={open} onOpenChange={onChange}>
      <ModalV2.Content
        variant='dark'
        fullscreen={false}
        onInteractOutside={(event) => {
          event.preventDefault();
        }}
      >
        <ModalV2.BodyTitle
          variant='dark'
          endEnhancer={<ModalV2.Close variant='dark' />}
        >
          <Body className='lg:text-[0.875rem] lg:leading-[1.25rem]'>
            {title}
          </Body>
        </ModalV2.BodyTitle>
        <HorizontalLine className='my-4 h-[0.2px] border-white-300' />
        <ModalV2.Body variant='dark'>
          <Body className='text-white-500 lg:text-[0.75rem] lg:leading-[1.125rem]'>
            {description}
          </Body>
        </ModalV2.Body>
      </ModalV2.Content>
    </ModalV2.Root>
  );
};
